import { AuthContextProvider, useAuth } from '@omni/kit/contexts/AuthContext';
import {
  Block,
  ComponentType,
  IInsetStyles,
  SimpleErrorBlock,
} from './blocks/types';
import Downloader, {
  IDownloaderResponse,
  NOT_MODIFIED,
} from '@omni/kit/Downloader';
import {
  EmitterSubscription,
  NativeEventEmitter,
  NativeModules,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { FallbackFileName, getFallbackFile } from './fallbacks';
import { KitText, KitTouchable } from '@omni/kit/components';
import LeftNavButton, { LeftNavType } from '@omni/kit/components/LeftNavButton';
import {
  NativeTopBarStyle,
  PresentationStyle,
  dismissReactNativeModal,
  getNativeTopBarHeight,
  setTopBarStyle,
  setTopBarTitle,
} from '@omni/kit/utilities/NativeHelpers';
import React, {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import {
  ScreenContextProvider,
  ScreenContextType,
  useScreenContext,
} from '@omni/kit/contexts/ScreenContext';
import {
  SimpleErrorBlocksWrapper,
  generateErrorPage,
} from './templates/examples/GeneratePage';
import ThemeContextWrapper, {
  ThemeContext,
} from '@omni/kit/theming/ThemeContext';

import Auth from '../auth/Auth';
import CacheService from '@omni/kit/services/CacheService';
import Colors from '@omni/kit/theming/Colors';
import { IRootProps } from '@omni/kit/contexts/types';
import KitList from '@omni/kit/components/KitList';
import KitLoader from '@omni/kit/components/KitLoader';
import LinearGradient from 'react-native-linear-gradient';
import { PresentationWrapper } from '@omni/kit/components/PresentationWrapper';
import { Provider } from 'react-redux';
import { RootSiblingParent } from 'react-native-root-siblings';
import { ScreenVisibilityContextProvider } from '@omni/kit/contexts/ScreenVisibilityContext';
import { ScriptureModalContextProvider } from '@omni/kit/contexts/ScriptureModalContext';
import Show from '@omni/kit/components/Show';
import { SizeClassContextProvider } from '@omni/kit/contexts/SizeClassContext';
import Spacing from '@omni/kit/theming/Spacing';
import { SpacingType } from '@omni/kit/theming/SpacingType';
import { getStyleForInsets } from './blocks/utilities';
import { renderBlock } from './blocks/resolver';
import store from '../messaging/shared/redux/store';

const debug = require('debug')('tca:blocks:BlockPage');

type BlocksWrapper = { blocks?: Block[]; title?: string };

export interface Props extends IRootProps {
  addressRequired?: boolean;
  animationEnabled?: boolean;
  clientBrandColor?: string;
  data?: any; // used to pass inline json data
  includeCloseButton?: boolean;
  includeLogoutButton?: boolean;
  initialBlockPageContextState?: ScreenContextType;
  largeSaveButtonEnabled?: boolean;
  onDismiss?: () => void;
  pageGradientEnabled?: boolean;
  fallbackResource?: string;
}

export default function BlockPage(props: Props): JSX.Element {
  const {
    addressRequired = false,
    animationEnabled = true,
    appKey,
    authProviderId,
    clientBrandColor,
    data,
    fallbackResource,
    guestToken,
    includeCloseButton = true,
    includeLogoutButton = true,
    initialBlockPageContextState,
    largeSaveButtonEnabled = false,
    leftNavButtonStyle,
    onDismiss = dismissReactNativeModal,
    pageGradientEnabled,
    presentationStyle,
    theme,
    topBarStyle,
    onScrollTopBarStyle,
    url,
  } = props;

  const { colorForScheme } = useContext(ThemeContext);

  return (
    <Provider store={store}>
      <AuthContextProvider
        props={{
          ...props,
          context: 'blockPage',
        }}
      >
        <ThemeContextWrapper theme={theme}>
          <SafeAreaProvider>
            <SizeClassContextProvider>
              <PresentationWrapper
                contentBackgroundColor={colorForScheme?.({
                  dark: Colors.N1000,
                  light: Colors.N0,
                })}
                presentationStyle={presentationStyle}
                onDismiss={onDismiss}
              >
                <ScriptureModalContextProvider>
                  <ScreenVisibilityContextProvider
                    nativeEventKey={props.nativeEventKey}
                  >
                    <BlockPageContent
                      addressRequired={addressRequired}
                      animationEnabled={animationEnabled}
                      appKey={appKey}
                      authProviderId={authProviderId}
                      clientBrandColor={clientBrandColor}
                      data={data}
                      fallbackResource={fallbackResource}
                      guestToken={guestToken}
                      includeCloseButton={includeCloseButton}
                      includeLogoutButton={includeLogoutButton}
                      initialBlockPageContextState={
                        initialBlockPageContextState
                      }
                      largeSaveButtonEnabled={largeSaveButtonEnabled}
                      leftNavButtonStyle={leftNavButtonStyle}
                      onDismiss={onDismiss}
                      pageGradientEnabled={pageGradientEnabled}
                      presentationStyle={presentationStyle}
                      topBarStyle={topBarStyle}
                      onScrollTopBarStyle={onScrollTopBarStyle}
                      url={url}
                    />
                  </ScreenVisibilityContextProvider>
                </ScriptureModalContextProvider>
              </PresentationWrapper>
            </SizeClassContextProvider>
          </SafeAreaProvider>
        </ThemeContextWrapper>
      </AuthContextProvider>
    </Provider>
  );
}

export const BlockPageContent = ({
  addressRequired = false,
  animationEnabled,
  appKey,
  authProviderId,
  clientBrandColor,
  data,
  fallbackResource,
  guestToken,
  includeCloseButton = true,
  initialBlockPageContextState,
  largeSaveButtonEnabled = false,
  leftNavButtonStyle,
  includeLogoutButton = true,
  onDismiss = dismissReactNativeModal,
  pageGradientEnabled,
  presentationStyle,
  topBarStyle,
  onScrollTopBarStyle,
  url,
}: {
  addressRequired?: boolean;
  animationEnabled?: boolean;
  appKey?: string;
  authProviderId?: string;
  clientBrandColor?: string;
  data: unknown;
  fallbackResource?: string;
  guestToken?: string;
  includeCloseButton?: boolean;
  includeLogoutButton?: boolean;
  initialBlockPageContextState?: ScreenContextType;
  largeSaveButtonEnabled?: boolean;
  leftNavButtonStyle?: LeftNavType;
  onDismiss?: (..._args: unknown[]) => void;
  pageGradientEnabled?: boolean;
  presentationStyle?: PresentationStyle;
  topBarStyle?: NativeTopBarStyle;
  onScrollTopBarStyle?: NativeTopBarStyle;
  url?: string;
}): JSX.Element => {
  const { accessToken, isAuthenticated } = useAuth();
  const [contentData, setContentData] = useState<BlocksWrapper | null>(null);
  const [errorCode, setErrorCode] = useState(0);
  const [errorData, setErrorData] = useState<SimpleErrorBlocksWrapper | null>(
    null
  );
  const [blocks, setBlocks] = useState<Array<Block | SimpleErrorBlock>>([]);
  const [isEdited, setIsEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authNavigatorVisible, setAuthNavigatorVisible] = useState(false);

  const [initialTopBarStyle, setInitialTopBarStyle] = useState(topBarStyle);
  const [currentTopBarStyle, setCurrentTopBarStyle] =
    useState(initialTopBarStyle);
  const [deltaInsetTop, setDeltaInsetTop] = useState(0);
  const [deltaInsetRetrieved, setDeltaInsetRetrieved] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { colorForScheme } = useContext(ThemeContext);

  const submitActionRef = useRef<() => void>() as MutableRefObject<() => void>;

  const insets = useSafeAreaInsets();
  const insetStyles: IInsetStyles = getStyleForInsets(insets);

  const _onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>): void => {
    if (initialTopBarStyle && initialTopBarStyle !== NativeTopBarStyle.Hidden) {
      if (
        event.nativeEvent.contentOffset.y < 10 &&
        event.nativeEvent.contentOffset.y > -30
      ) {
        setCurrentTopBarStyle(initialTopBarStyle);
      } else setCurrentTopBarStyle(onScrollTopBarStyle);
    }
  };

  const _renderBlock = ({
    item,
    index,
  }: {
    item: Block;
    index: number;
  }): JSX.Element | null => {
    let insetStyle: ViewStyle = insetStyles.mid;

    // apply inset for first block
    if (index === 0) {
      insetStyle = {
        ...insetStyles.top,
        paddingTop: insets.top + deltaInsetTop,
      };
    }

    // apply inset for last block
    if (index === blocks.length - 1) {
      insetStyle = {
        // ARTEMIS-1237: Preserve top inset for case where page has a single block.
        ...insetStyle,

        ...insetStyles.bottom,
      };
    }

    const componentProps = { ...item.props, index, insetStyle };

    return renderBlock(item, componentProps, guestToken, appKey, {
      addressRequired,
      clientBrandColor,
      includeLogoutButton,
      isLoading,
      largeSaveButtonEnabled,
      onDismiss,
      setIsEdited,
      setIsLoading,
      setRefreshing,
      submitActionRef,
    });
  };

  const _loadCachedFeed = async (): Promise<IDownloaderResponse | void> => {
    if (!url) return;

    return await CacheService.getCachedResponse({
      url: url,
      authorization: accessToken,
    });
  };

  const _downloadFeed = (
    cachedResponse?: IDownloaderResponse,
    impression?: boolean
  ): void => {
    if (!url) return;

    const context = 'network';

    const extraHeaders: { Authorization?: string } = {};

    if (accessToken && authProviderId) {
      extraHeaders.Authorization = accessToken;
    }

    Downloader.downloadUrl({
      url,
      options: {
        appKey: appKey,
        authProviderId: authProviderId,
        extraHeaders,
        impression: impression,
        method: 'GET',
      },
      cachedResponse,
    })
      .then((response) => {
        _handleFeedResponse(response, context);
        setRefreshing(false);
      })
      .catch((err) => {
        const status = (err?.response?.status as number) || 504;
        _handleFeedLoadError(status, err, context);
        setRefreshing(false);
      });
  };

  const _handleFeedLoadError = (
    code: number,
    err?: {
      response: {
        headers?: Record<string, unknown>;
        request?: { responseURL: string };
      };
    },
    context?: string
  ) => {
    debug(
      'Error loading feed data',
      '| Context:',
      context,
      '| Code:',
      code,
      '| Error:',
      err
    );

    if (
      NativeModules.ReactPlatformBridge != null &&
      (code === 401 || code === 403)
    ) {
      debug('Prompt user to login with AuthNavigator');
      setAuthNavigatorVisible(true);

      /**
       * 'setErrorCode' causes an error screen to render,
       * so here we are opting out of that error code/screen for the case where
       * we are prompting a user to login
       */
    } else {
      setErrorCode(code);
    }
  };

  const _handleErrorCode = (code: number): void => {
    if (code) {
      let fallback: BlocksWrapper | undefined;

      if (fallbackResource) {
        fallback = getFallbackFile(
          fallbackResource as FallbackFileName
        ) as BlocksWrapper;
      }

      if (fallback?.blocks && !contentData) {
        setContentData(fallback as BlocksWrapper);
      } else {
        const data = generateErrorPage(code, (): void => {
          setErrorCode(0); // dismiss error
          _downloadFeed(undefined, false); // retry feed request
        });

        setErrorData(data);
      }
    } else {
      setErrorData(null);
    }
  };

  const _handleFeedResponse = async (
    response?: { body?: BlocksWrapper; status?: number },
    context?: string
  ) => {
    if (!response && context === 'cache') {
      debug('cache miss');

      return;
    }

    debug(
      `Handle response. Context: ${context} Status: ${response?.status}. ` +
        `URL: ${url}`
    );

    if (response?.status === NOT_MODIFIED) return;

    let status = response?.status ?? 550;
    const body = response?.body ?? null;

    if (status < 400 && !body?.blocks) {
      status = 550;
      debug('Response data is malformed.  No blocks found.');
    }

    if (status < 400) {
      setContentData(body);
    } else {
      _handleFeedLoadError(status, undefined, context);
    }
  };

  useEffect((): void => {
    const dataObject = typeof data === 'string' ? JSON.parse(data) : data;

    if (dataObject?.blocks) {
      setContentData(dataObject as BlocksWrapper);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    _loadCachedFeed().then((cachedResponse) => {
      _handleFeedResponse(cachedResponse as IDownloaderResponse, 'cache');
      _downloadFeed(cachedResponse as IDownloaderResponse, true);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Platform.OS === 'android') {
      getNativeTopBarHeight()
        .then((height) => {
          setDeltaInsetTop(height);
          setDeltaInsetRetrieved(true);
        })
        .catch(() => {
          setDeltaInsetRetrieved(true);
        });
    } else {
      setDeltaInsetRetrieved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * TODO: move this native auth error event listener to AuthContext.
   * Keeping here for now to reduce regression risk in 5.17
   */
  useEffect(() => {
    const listeners = _registerEmitters();

    return () => {
      listeners?.forEach((listener) => {
        listener?.remove();
      });
    };
  }, []);

  const _registerEmitters = (): EmitterSubscription[] | void => {
    if (!NativeModules.ReactPlatformBridge) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      NativeModules.ReactPlatformBridge
    );

    const authorizationFailedSubscription =
      ReactPlatformBridgeEventEmitter.addListener(
        'AuthorizationError',
        (_error) => setErrorCode(401)
      );

    return [authorizationFailedSubscription];
  };

  useEffect(() => {
    if (authProviderId && accessToken && isAuthenticated) {
      debug('Authorization changed, reloading...');
      authNavigatorVisible && setAuthNavigatorVisible(false);

      _loadCachedFeed().then((cachedResponse) => {
        _downloadFeed(cachedResponse as IDownloaderResponse, false);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode, accessToken, isAuthenticated]);

  useEffect(() => {
    _handleErrorCode(errorCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  useEffect(() => {
    const dataObject = typeof data === 'string' ? JSON.parse(data) : data;

    if (dataObject?.blocks) {
      setContentData(dataObject as BlocksWrapper);
    }
  }, [data]);

  useEffect(() => {
    if (currentTopBarStyle) {
      setTopBarStyle(
        currentTopBarStyle,
        false /* updateInsets */,
        true /* animated */
      );
    }
  }, [currentTopBarStyle]);

  useEffect(() => {
    const { ReactPlatformBridge } = NativeModules;

    if (!ReactPlatformBridge) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      ReactPlatformBridge
    );

    const listener = ReactPlatformBridgeEventEmitter.addListener(
      'NotifyAppUserSettingsUpdated',
      () => {
        _onRefresh();
      }
    );

    return () => {
      listener?.remove();
    };
  }, []);

  useEffect(() => {
    if (initialTopBarStyle) {
      setTopBarStyle(
        initialTopBarStyle,
        true /* updateInsets */,
        true /* animated */
      );
    }
  }, [initialTopBarStyle]);

  useEffect(() => {
    if (deltaInsetRetrieved && (contentData || errorData)) {
      const title = contentData?.title ?? errorData?.title;

      if (title) setTopBarTitle(title);

      const contentBlocks = contentData?.blocks ?? errorData?.blocks;

      if (!contentBlocks) return;

      /**
       * Media banner is currently the only block that supports transparent top
       * bar style. If it's not the first block, the transparent top bar style
       * must be suppressed.
       */
      if (
        initialTopBarStyle &&
        initialTopBarStyle !== NativeTopBarStyle.Hidden
      ) {
        setInitialTopBarStyle(
          contentBlocks.length &&
            contentBlocks[0].type === ComponentType.MediaBanner
            ? topBarStyle
            : NativeTopBarStyle.Default
        );
      }

      setBlocks(contentBlocks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deltaInsetRetrieved, contentData, errorData]);

  useEffect(() => {
    if (refreshing) {
      _loadCachedFeed().then((cachedResponse) => {
        _downloadFeed(cachedResponse as IDownloaderResponse, false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const _onRefresh = () => {
    setRefreshing(true);
  };

  const onSavePress = () => {
    submitActionRef.current();
  };

  const CloseAndSaveButtons = (): JSX.Element => {
    // TODO: Can this assignment be removed?
    const { edgeSpacing: _edgeSpacing } = useScreenContext({
      fixedSpacingType: Platform.OS === 'web' ? SpacingType.Small : undefined,
    });
    // Optionally show save button in navbar (vs. bottom of component).
    const includeSaveButton = !largeSaveButtonEnabled;

    return (
      <View>
        <View
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginHorizontal: Spacing.xl,
            marginTop: insets.top,
          }}
        >
          <Show show={includeCloseButton}>
            <LeftNavButton
              onPress={() => onDismiss()}
              title=''
              iconSize={20}
              type={
                leftNavButtonStyle === LeftNavType.Back
                  ? LeftNavType.Back
                  : LeftNavType.Dismiss
              }
            />
          </Show>

          <Show show={includeSaveButton}>
            <>
              <KitTouchable
                onPress={isEdited ? onSavePress : undefined}
                style={{
                  marginLeft: !includeCloseButton ? 'auto' : undefined,
                  opacity: isEdited ? 1 : 0,
                }}
              >
                <KitText
                  brandColor
                  semiBold
                  fontSize={17}
                  testID={isEdited ? 'SaveText' : 'SaveTextHidden'}
                >
                  Save
                </KitText>
              </KitTouchable>

              <Show show={isLoading}>
                <KitLoader small style={{ marginRight: Spacing.s }} />
              </Show>
            </>
          </Show>
        </View>
      </View>
    );
  };

  if (authNavigatorVisible) {
    return (
      <Auth appKey={appKey ?? ''} targetAuthProviderId={authProviderId ?? ''} />
    );
  }

  return (
    <RootSiblingParent>
      <ScreenContextProvider
        initialState={initialBlockPageContextState}
        style={{
          ...(presentationStyle !== PresentationStyle.KitModal && { flex: 1 }),
          backgroundColor: colorForScheme?.({
            dark: Colors.N1000,
            light: Colors.N0,
          }),
        }}
      >
        {pageGradientEnabled && (
          <LinearGradient
            colors={[Colors.N0, Colors.N50]}
            style={{ position: 'absolute', height: '100%', width: '100%' }}
          />
        )}
        <Show show={topBarStyle === NativeTopBarStyle.Hidden}>
          <CloseAndSaveButtons />
        </Show>
        <KitList
          animationEnabled={animationEnabled}
          data={blocks as Block[]}
          keyboardShouldPersistTaps='handled'
          keyExtractor={(_, index) => index.toString()}
          onRefresh={url ? _onRefresh : null}
          onScroll={_onScroll}
          refreshing={refreshing}
          renderItem={_renderBlock}
          scrollEventThrottle={5}
        />
        {blocks?.length === 0 && (
          <LinearGradient
            colors={['#00000033', '#00000000']}
            style={styles.gradient}
          />
        )}
        {blocks?.length === 0 && (
          <View style={styles.loadingIndicator}>
            <KitLoader />
          </View>
        )}
      </ScreenContextProvider>
    </RootSiblingParent>
  );
};

const styles = StyleSheet.create({
  gradient: {
    backgroundColor: '#ffffff00',
    height: 200,
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 5,
  },
  loadingIndicator: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
